import { useEffect, useState, useRef, useCallback } from 'react';
import { totalCountEvent } from 'services/generalServices';
import { listActivityTypes } from 'services/timerServices';
import { useDataProvider } from 'context/DataProviderContext';

const useActivityTypes = (initialFilters = {}, skipInitialLoad = false, excludeFromLoading = false) => {
  const { activityTypes: cachedActivityTypes, setActivityTypes: setCachedActivityTypes } = useDataProvider();

  // The DataProvider session cache is ONLY safe to use when excludeFromLoading === true,
  // which uniquely identifies TimerManager's call site. Every other caller uses different
  // filter combinations (category: 'automated', no category, etc.), so sharing a single
  // cache array across them would return wrong data (e.g. ActivityMapping would receive
  // manual types instead of automated ones). Non-cached callers always fetch independently.
  const useSessionCache = excludeFromLoading === true;

  const [activityTypes, setActivityTypesLocal] = useState(() =>
    useSessionCache && cachedActivityTypes?.length > 0 ? cachedActivityTypes : []
  );
  const [totalCount, setTotalCount] = useState(0);
  const initialFiltersRef = useRef(initialFilters);

  useEffect(() => {
    initialFiltersRef.current = initialFilters;
  }, [initialFilters]);

  // For cached callers: keep local state in sync when DataProvider cache is
  // populated on another mount or cleared on logout.
  useEffect(() => {
    if (useSessionCache) {
      setActivityTypesLocal(cachedActivityTypes || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cachedActivityTypes]);

  const loadActivityTypes = useCallback(
    async (practiceAreaIds = null) => {
      try {
        const count = totalCount || (await totalCountEvent('activity_type'))?.data;
        if (!count) return;

        setTotalCount(count);

        const filters = { ...initialFiltersRef.current };
        if (practiceAreaIds && practiceAreaIds.length > 0) {
          filters.practice_area_ids = practiceAreaIds;
        }

        const response = await listActivityTypes('', count, '', 'forward', filters, excludeFromLoading);
        if (response && response.data && response.data?.activity_types.length > 0) {
          response.data.activity_types.forEach((type) => {
            type.activity_label = type.activity_type + ' - ' + type.activity_type_code;
          });

          const fetched = [...response.data.activity_types];
          setActivityTypesLocal(fetched);
          // Only persist in DataProvider for the TimerManager use-case so that other
          // callers with different filters are never served stale/wrong data.
          if (useSessionCache) {
            setCachedActivityTypes(fetched);
          }
        }
      } catch (error) {
        console.error('Error loading activity types:', error);
      }
    },
    [totalCount, useSessionCache, setCachedActivityTypes]
  );

  useEffect(() => {
    if (skipInitialLoad) return;
    // For cached callers (TimerManager): skip if DataProvider already has data.
    // For all other callers: always fetch to get their own filter-specific data.
    if (useSessionCache && cachedActivityTypes?.length > 0) return;
    loadActivityTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { activityTypes, loadActivityTypes };
};

export default useActivityTypes;
